<template>
  <div class="dashboard-layout h-screen flex flex-col">
    <Header 
      title="Data Import" 
      :showSearch="false" 
      :showMenuToggle="true" 
      @toggle-sidebar="toggleSidebar"
    />
    
    <div class="flex flex-1 overflow-hidden">
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-6 transition-all duration-300"
      >
        <div class="data-import-container">
          <div class="flex items-center justify-between mb-6">
            <h1 class="text-2xl font-bold text-gray-800">Data Import</h1>
          </div>

          <!-- Data Import Card -->
          <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 class="text-lg font-semibold mb-4">Import Property Data</h2>
            <p class="text-gray-600 mb-4">
              Upload your property data in various formats to visualize on the map. 
              Supported formats include GeoJSON, CSV, KML, and other spatial data formats.
            </p>

            <!-- File Upload Section -->
            <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 text-center hover:bg-gray-50 transition-colors" 
                 @dragover.prevent 
                 @drop.prevent="onFileDrop">
              <div v-if="!selectedFile" class="space-y-3">
                <i class="fas fa-upload text-3xl text-primary-500"></i>
                <p class="text-gray-600">Drag and drop your file here, or</p>
                <label for="file-upload" class="cursor-pointer bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-md inline-block transition-colors">
                  Browse Files
                  <input 
                    id="file-upload" 
                    type="file" 
                    class="hidden" 
                    @change="onFileSelect"
                    accept=".geojson,.csv,.kml,.gpx,.shp,.zip,.json"
                  >
                </label>
                <p class="text-xs text-gray-500">Supported formats: GeoJSON, CSV, KML, GPX, Shapefile (zipped), JSON</p>
              </div>
              <div v-else class="space-y-3">
                <i class="fas fa-file-alt text-3xl text-primary-500"></i>
                <p class="text-gray-800 font-medium">{{ selectedFile.name }}</p>
                <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                <div class="flex justify-center space-x-3">
                  <button @click="uploadFile" class="bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-md transition-colors">
                    Upload
                  </button>
                  <button @click="selectedFile = null" class="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- Format Guide -->
            <div class="bg-gray-50 p-4 rounded-lg">
              <h3 class="font-medium text-gray-800 mb-2">Data Format Guide</h3>
              
              <div class="space-y-3">
                <div>
                  <h4 class="font-medium text-gray-700">GeoJSON</h4>
                  <p class="text-sm text-gray-600">A standard format for representing geographical features. Your GeoJSON file should include properties with the following data:</p>
                  <pre class="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
{
  "type": "FeatureCollection",
  "features": [{
    "type": "Feature",
    "geometry": { "type": "Point", "coordinates": [36.8219, -1.2921] },
    "properties": {
      "title": "Property Name",
      "address": "Property Address",
      "price": 10000000,
      "size": 2500,
      "bedrooms": 3,
      "bathrooms": 2,
      "payment_status": "paid" // or "unpaid"
    }
  }]
}
                  </pre>
                </div>
                
                <div>
                  <h4 class="font-medium text-gray-700">CSV</h4>
                  <p class="text-sm text-gray-600">CSV files should include latitude and longitude columns along with property data. Example format:</p>
                  <pre class="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
title,address,price,size,bedrooms,bathrooms,latitude,longitude,payment_status
"Property Name","Property Address",10000000,2500,3,2,36.8219,-1.2921,"paid"
                  </pre>
                </div>
                
                <div>
                  <h4 class="font-medium text-gray-700">KML</h4>
                  <p class="text-sm text-gray-600">KML files should have proper placemarks with extended data for property details.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Manual Data Entry -->
          <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 class="text-lg font-semibold mb-4">Manual Data Entry</h2>
            <p class="text-gray-600 mb-4">
              Alternatively, you can manually input property data using the form below.
            </p>
            
            <form @submit.prevent="submitManualData" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Property Title</label>
                <input v-model="manualData.title" type="text" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Address</label>
                <input v-model="manualData.address" type="text" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Price (KES)</label>
                <input v-model.number="manualData.price" type="number" min="0" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Size (sq. ft)</label>
                <input v-model.number="manualData.size" type="number" min="0" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Bedrooms</label>
                <input v-model.number="manualData.bedrooms" type="number" min="0" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Bathrooms</label>
                <input v-model.number="manualData.bathrooms" type="number" min="0" step="0.5" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Latitude</label>
                <input v-model.number="manualData.latitude" type="number" step="0.000001" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Longitude</label>
                <input v-model.number="manualData.longitude" type="number" step="0.000001" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Payment Status</label>
                <select v-model="manualData.payment_status" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Description</label>
                <textarea v-model="manualData.description" class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" rows="3"></textarea>
              </div>
              
              <div class="md:col-span-2 mt-4">
                <button type="submit" class="bg-primary-600 text-white py-2 px-6 rounded-md hover:bg-primary-700 transition-colors">
                  Add Property
                </button>
              </div>
            </form>
          </div>

          <!-- Import History -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <h2 class="text-lg font-semibold mb-4">Import History</h2>
            <p v-if="importHistory.length === 0" class="text-gray-500 italic">No import history available</p>
            
            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Properties</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(item, index) in importHistory" :key="index">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.filename }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.format }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.properties }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.date }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="{
                        'bg-green-100 text-green-800': item.status === 'Success',
                        'bg-red-100 text-red-800': item.status === 'Failed',
                        'bg-yellow-100 text-yellow-800': item.status === 'Processing'
                      }" class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full">
                        {{ item.status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../stores/auth';
import Header from '../components/Header.vue';
import Sidebar from '../components/Sidebar.vue';

const authStore = useAuthStore();
const selectedFile = ref(null);
const importHistory = ref([]);
const sidebarCollapsed = ref(false);

const manualData = ref({
  title: '',
  address: '',
  price: null,
  size: null,
  bedrooms: null,
  bathrooms: null,
  latitude: 36.8219, // Default to Nairobi, Kenya
  longitude: -1.2921,
  payment_status: 'unpaid',
  description: ''
});

// Toggle sidebar
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

// Handle file selection
const onFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
  }
};

// Handle file drop
const onFileDrop = (event) => {
  const file = event.dataTransfer.files[0];
  if (file) {
    selectedFile.value = file;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / 1048576).toFixed(1) + ' MB';
};

// Upload file to server
const uploadFile = async () => {
  if (!selectedFile.value) return;
  
  try {
    const formData = new FormData();
    formData.append('file', selectedFile.value);
    
    const response = await axios.post('/api/data/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${authStore.token}`
      }
    });
    
    if (response.data && response.data.success) {
      alert('File uploaded successfully!');
      selectedFile.value = null;
      fetchImportHistory();
    }
  } catch (error) {
    alert(`Upload failed: ${error.response?.data?.message || 'Unknown error'}`);
    console.error('Error uploading file:', error);
  }
};

// Submit manual data entry
const submitManualData = async () => {
  try {
    const response = await axios.post('/api/properties', manualData.value, {
      headers: {
        'Authorization': `Bearer ${authStore.token}`
      }
    });
    
    if (response.data && response.data.success) {
      alert('Property added successfully!');
      
      // Reset form
      manualData.value = {
        title: '',
        address: '',
        price: null,
        size: null,
        bedrooms: null,
        bathrooms: null,
        latitude: 36.8219,
        longitude: -1.2921,
        payment_status: 'unpaid',
        description: ''
      };
    }
  } catch (error) {
    alert(`Adding property failed: ${error.response?.data?.message || 'Unknown error'}`);
    console.error('Error adding property:', error);
  }
};

// Fetch import history
const fetchImportHistory = async () => {
  try {
    const response = await axios.get('/api/data/import/history', {
      headers: {
        'Authorization': `Bearer ${authStore.token}`
      }
    });
    
    if (response.data && response.data.success) {
      importHistory.value = response.data.history || [];
    }
  } catch (error) {
    console.error('Error fetching import history:', error);
  }
};

// Fetch initial data
fetchImportHistory();
</script>